/**
 * AdminMangaStudio — owner-only manga panel generator using Nano Banana.
 * - Lists all drills with current panel source (AI / static / missing)
 * - Generate or regenerate panels per drill
 * - Revert AI override back to static panels
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Sparkles, RefreshCw, Trash2, Eye, AlertCircle, ImagePlus, Crown, ArrowLeft, Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOURCE_BADGE = {
  ai: { label: "AI", color: "#FF4500" },
  static: { label: "STATIC", color: "#9CA3AF" },
  missing: { label: "MISSING", color: "#ef4444" },
};

const AdminMangaStudio = () => {
  const navigate = useNavigate();
  const [drills, setDrills] = useState([]);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [generating, setGenerating] = useState(null);
  const [previewDrill, setPreviewDrill] = useState(null);
  const [previewPanels, setPreviewPanels] = useState(null);
  const [error, setError] = useState("");

  const fetchDrills = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/manga/drills`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDrills(res.data.drills || []);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
      else setError(e.response?.data?.detail || "Failed to load drills");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDrills();
  }, []);

  const generate = async (drillName) => {
    setGenerating(drillName);
    setError("");
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/manga/generate`,
        { drill_name: drillName },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 }
      );
      await fetchDrills();
    } catch (e) {
      setError(e.response?.data?.detail || `Failed to generate ${drillName}`);
    } finally {
      setGenerating(null);
    }
  };

  const revert = async (drillName) => {
    if (!window.confirm(`Revert "${drillName}" back to static panels? AI override will be deleted.`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/manga/drill/${encodeURIComponent(drillName)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchDrills();
    } catch (e) {
      setError(e.response?.data?.detail || "Revert failed");
    }
  };

  const openPreview = async (drillName) => {
    setPreviewDrill(drillName);
    setPreviewPanels(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API}/admin/manga/drill/${encodeURIComponent(drillName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewPanels(res.data);
    } catch (e) {
      setPreviewPanels({ error: e.response?.data?.detail || "Not available" });
    }
  };

  if (forbidden) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-[#FF4500] mx-auto mb-4" />
          <h1 className="text-3xl font-heading text-white uppercase mb-2">Access Denied</h1>
          <p className="text-white/40 text-sm mb-6">Owner-only studio.</p>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-[#FF4500] hover:bg-[#FF6326] text-white px-6 py-2 font-heading uppercase tracking-wider text-xs"
            data-testid="back-to-dash-btn"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const filtered = drills.filter((d) => filter === "all" || d.source === filter);
  const counts = {
    all: drills.length,
    ai: drills.filter((d) => d.source === "ai").length,
    static: drills.filter((d) => d.source === "static").length,
    missing: drills.filter((d) => d.source === "missing").length,
  };

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-6 lg:p-8" data-testid="admin-manga-studio">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-6xl mx-auto space-y-6"
      >
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <button
              onClick={() => navigate("/admin")}
              className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 hover:text-[#FF4500] flex items-center gap-1 mb-3"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="w-3 h-3" /> Admin
            </button>
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-4 h-4 text-[#FF4500]" />
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4500]">
                Owner Console
              </p>
            </div>
            <h1 className="text-5xl md:text-6xl font-heading text-white uppercase tracking-tight leading-none">
              Manga Studio
            </h1>
            <p className="text-[11px] font-mono text-white/40 mt-2">
              Nano Banana · Gemini 3.1 Flash Image
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-px bg-white/[0.06] flex-wrap">
          {["all", "ai", "static", "missing"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`flex-1 min-w-[120px] py-3 px-4 text-xs font-mono uppercase tracking-[0.2em] ${
                filter === f ? "bg-[#FF4500] text-white" : "bg-[#0A0A0A] text-white/50 hover:text-white"
              }`}
              data-testid={`filter-${f}-btn`}
            >
              {f} <span className="opacity-50 ml-1">({counts[f]})</span>
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 p-3 text-xs text-red-400 font-mono" data-testid="error-msg">
            {error}
          </div>
        )}

        {/* Drill list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 text-[#FF4500] animate-spin" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06]">
            {filtered.map((d) => {
              const badge = SOURCE_BADGE[d.source];
              const isGenerating = generating === d.name;
              return (
                <div
                  key={d.name}
                  className="bg-[#0A0A0A] p-4 flex flex-col gap-3"
                  data-testid={`drill-card-${d.name.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-semibold truncate">{d.name}</p>
                      <p className="text-[10px] font-mono text-white/30 uppercase tracking-widest mt-0.5">
                        {d.category} · phase {d.phase} · {d.panel_count} panels
                      </p>
                    </div>
                    <span
                      className="text-[9px] font-mono uppercase tracking-[0.2em] px-2 py-1"
                      style={{ background: `${badge.color}22`, color: badge.color }}
                    >
                      {badge.label}
                    </span>
                  </div>

                  <div className="flex gap-2 mt-auto">
                    <button
                      onClick={() => openPreview(d.name)}
                      className="flex-1 bg-white/[0.04] hover:bg-white/[0.08] text-white text-[10px] font-mono uppercase tracking-widest py-2 flex items-center justify-center gap-1.5"
                      data-testid={`preview-${d.name.replace(/\s+/g, '-').toLowerCase()}-btn`}
                    >
                      <Eye className="w-3 h-3" /> View
                    </button>
                    <button
                      disabled={isGenerating || d.source === "missing"}
                      onClick={() => generate(d.name)}
                      className="flex-1 bg-[#FF4500] hover:bg-[#FF6326] disabled:opacity-30 disabled:cursor-not-allowed text-white text-[10px] font-mono uppercase tracking-widest py-2 flex items-center justify-center gap-1.5"
                      data-testid={`generate-${d.name.replace(/\s+/g, '-').toLowerCase()}-btn`}
                    >
                      {isGenerating ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : d.source === "ai" ? (
                        <RefreshCw className="w-3 h-3" />
                      ) : (
                        <Sparkles className="w-3 h-3" />
                      )}
                      {isGenerating ? "Gen…" : d.source === "ai" ? "Regen" : "AI"}
                    </button>
                    {d.source === "ai" && (
                      <button
                        onClick={() => revert(d.name)}
                        className="bg-white/[0.04] hover:bg-red-500/20 hover:text-red-400 text-white/50 text-[10px] font-mono py-2 px-2"
                        data-testid={`revert-${d.name.replace(/\s+/g, '-').toLowerCase()}-btn`}
                        title="Revert to static panels"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Preview modal */}
        {previewDrill && (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => setPreviewDrill(null)}
            data-testid="preview-modal"
          >
            <div
              className="bg-[#0A0A0A] border border-white/[0.06] max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4500]">
                    {previewPanels?.source === "ai" ? "AI Generated" : "Static"}
                  </p>
                  <h2 className="text-2xl font-heading text-white uppercase">{previewDrill}</h2>
                </div>
                <button
                  onClick={() => setPreviewDrill(null)}
                  className="text-white/40 hover:text-white text-xs font-mono uppercase tracking-widest"
                  data-testid="close-preview-btn"
                >
                  Close
                </button>
              </div>
              {!previewPanels ? (
                <Loader2 className="w-5 h-5 text-[#FF4500] animate-spin mx-auto my-12" />
              ) : previewPanels.error ? (
                <p className="text-red-400 text-sm py-12 text-center">{previewPanels.error}</p>
              ) : (
                <div className="grid sm:grid-cols-3 gap-3">
                  {previewPanels.panels?.map((p) => (
                    <div key={p.step} className="bg-black border border-white/[0.06]">
                      <img
                        src={`${BACKEND_URL}${p.image}`}
                        alt={`${previewDrill} step ${p.step}`}
                        className="w-full aspect-square object-cover"
                        loading="lazy"
                      />
                      <div className="p-3">
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#FF4500]">
                          Step {p.step} · {p.label}
                        </p>
                        <p className="text-xs text-white/70 mt-1">{p.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminMangaStudio;
